"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { href: '/homepage', label: '首頁', labelEn: 'Home' },
  { href: '/about', label: '關於我們', labelEn: 'About' },
  { href: '/business-scope', label: '業務範圍', labelEn: 'Business' },
  { href: '/contact', label: '聯繫我們', labelEn: 'Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-brand-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-container mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <AppLogo
            src="/assets/images/image-2026-02-27-210222-1773023770542.png"
            size={52}
            className="rounded-lg overflow-hidden"
          />
          <div className="hidden sm:block">
            <p className="font-heading font-700 text-primary text-base leading-tight">信悅日用品</p>
            <p className="text-xs text-brand-muted font-body tracking-wider">XINYUEE</p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? 'text-primary' :'text-brand-fg/70 hover:text-primary'
                }`}
              >
                {link?.label}
                <span
                  className={`absolute bottom-1 left-4 right-4 h-0.5 rounded-full bg-primary transition-all duration-300 ${
                    isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/registration-center"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
              pathname === '/registration-center' ?'bg-primary text-white shadow-md' :'bg-primary text-white hover:bg-primary-dark shadow-sm hover:shadow-md hover:-translate-y-0.5'
            }`}
          >
            立即報名
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 rounded-lg text-brand-fg hover:bg-secondary transition-colors"
          aria-label="Toggle menu"
        >
          <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
        </button>
      </div>
      {/* Mobile menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{ background: 'rgba(253, 250, 246, 0.97)', backdropFilter: 'blur(12px)' }}
      >
        <nav className="px-6 pb-6 pt-2 flex flex-col gap-1 border-t border-brand-border">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-secondary text-primary' :'text-brand-fg/80 hover:bg-secondary/60 hover:text-primary'
                }`}
              >
                {link?.label}
                <span className="ml-2 text-xs text-brand-muted">{link?.labelEn}</span>
              </Link>
            );
          })}
          <Link
            href="/registration-center"
            className="mt-3 px-4 py-3 bg-primary text-white rounded-xl text-base font-semibold text-center hover:bg-primary-dark transition-colors"
          >
            立即報名合作
          </Link>
        </nav>
      </div>
    </header>
  );
}